import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../service/auth.service';
import { SessionExpireComponent } from '../session-expire/session-expire.component';
import { BroadcastChannelService } from './broadcast-channel.service';

@Injectable({
  providedIn: 'root'
})
export class IntervalSessionService {
  private readonly sessionExpireNotify: number = 5 * 60 * 1000; // Notify 5 minutes before expiration // number = 5 * 1000; // Notify 5 seconds before expiration (testing value)
  private readonly inactivityTimeout: number = 25 * 60 * 1000; // Inactivity timeout (25 minutes) // number = 30 * 1000; // Inactivity timeout (30 seconds testing)
  private expirationTimer: Subscription | null = new Subscription(); // Timer to track session expiration
  private inactivityTimer: any; // Timer to track inactivity
  private refreshTimer: any; // Timer to proactively refresh the token before it expires
  private readonly proactiveRefreshBuffer: number = 2 * 60 * 1000; // Refresh 2 minutes before expiration
  private dialogOpen: boolean = false; // Flag to prevent multiple dialogs
  private dialogOpenTimer: any; // Timer to track how long the dialog is open
  private isExtendingSession: boolean = false;
  private sessionExpireDialogRef: MatDialogRef<SessionExpireComponent> | null = null;

  constructor(private readonly dialog: MatDialog, private readonly authService: AuthService, private readonly broadcastChannelService: BroadcastChannelService) {
    // Only start session expiration check if the user is logged in
    const expirationTime = parseInt(localStorage.getItem('sessionExpiration') || '0', 10);
    if (this.isSessionValid(expirationTime)) {
      this.startSessionExpirationCheck();
    }

    // Listen for storage events to synchronize session expiration across browser tabs/windows
    window.addEventListener('storage', (event) => this.handleStorageEvent(event));
  }

  /**
 * Checks if the session is valid based on the expiration time.
 */
  private isSessionValid(expirationTime: number): boolean {
    return expirationTime > Date.now();
  }

  /**
 * Returns true only when the user is actually authenticated (both a verifyId
 * and an access token are present). Prevents refresh/dialog activity pre-login.
 */
  private isAuthenticated(): boolean {
    return !!this.authService.getVerifyId() && !!sessionStorage.getItem(environment.x_auth_token_header);
  }

  /**
   * Starts the session expiration check based on the expiration time stored in localStorage.
   */
  private startSessionExpirationCheck(): void {
    const expirationTime = parseInt(localStorage.getItem('sessionExpiration') || '0', 10);

    if (!expirationTime || expirationTime <= Date.now() + 5000) {
      console.error('Session has already expired or expiration time is invalid.');
      this.logout();
      return;
    }

    // Clear any existing timer to avoid duplicate checks
    if (this.expirationTimer) {
      this.expirationTimer.unsubscribe();
    }

    // Dynamically adjust the check interval based on remaining time
    const timeLeft = expirationTime - Date.now();

    const interval = timeLeft > this.sessionExpireNotify ? 60000 : 10000; // Check every 60 seconds if > 5 minutes left, else every 10 seconds

    // Start a periodic timer to check session expiration
    this.expirationTimer = timer(0, interval).subscribe(() => {
      const storedExpiration = parseInt(localStorage.getItem('sessionExpiration') || '0', 10);
      const timeLeft = storedExpiration - Date.now();

      // Open the session expiration dialog 5 minutes before expiration
      if (timeLeft <= this.sessionExpireNotify && !this.dialogOpen) {
        this.openSessionExpireDialog();
      }

      // Logout the user when the session expires
      if (timeLeft <= 0) {
        console.error('Session has already expired');
        this.logout();
      }
    });

    // Schedule a proactive refresh so the token is renewed before it expires
    this.scheduleProactiveRefresh();
  }

  /**
   * Schedules a proactive token refresh shortly before the session expires so the
   * access token stays valid during active use and never expires silently.
   */
  public scheduleProactiveRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const expirationTime = parseInt(localStorage.getItem('sessionExpiration') || '0', 10);
    if (!expirationTime) {
      return;
    }

    const timeLeft = expirationTime - Date.now();
    const delay = Math.max(timeLeft - this.proactiveRefreshBuffer, 0);
    this.refreshTimer = setTimeout(() => this.proactiveRefresh(), delay);
  }

  /**
   * Silently refreshes the token before expiry. Only runs when the user is
   * authenticated (both verifyId and access token present).
   */
  private proactiveRefresh(): void {
    if (!this.isAuthenticated()) {
      return; // Not authenticated yet; do not attempt a refresh
    }

    if (this.isExtendingSession) {
      return;
    }

    this.isExtendingSession = true;
    this.authService.refreshToken().subscribe({
      next: (response) => {
        const jwtExpiresAt = Number(response.expiresAt);
        if (jwtExpiresAt) {
          localStorage.setItem('sessionExpiration', jwtExpiresAt.toString());
        }
        this.isExtendingSession = false;
        // Intentionally do NOT reset the inactivity timer here: a proactive,
        // automated refresh is not user activity and must not prevent the
        // inactivity dialog / auto-logout from firing.
        this.scheduleProactiveRefresh();
      },
      error: (err) => {
        console.error('Proactive session refresh failed:', err?.status);
        this.isExtendingSession = false;
      }
    });
  }

  /**
   * Resets the inactivity timer whenever an API call is made.
   */
  resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (typeof this.inactivityTimeout === 'number' && this.inactivityTimeout > 0) {
      this.inactivityTimer = setTimeout(() => {
        if (this.authService.getVerifyId()) {
          this.promptSessionExtension();
        }
      }, this.inactivityTimeout);
    } else {
      console.error("Invalid inactivityTimeout value:", this.inactivityTimeout);
    }
  }

  /**
   * Prompts the user to extend their session after inactivity.
   */
  private promptSessionExtension(): void {
    if (!this.dialogOpen) {
      this.openSessionExpireDialog();
    }
  }

  /**
   * Opens the session expiration dialog to notify the user.
   */
  public openSessionExpireDialog(): void {
    if (!this.isAuthenticated()) {
      return; // Do not show the session dialog before the user has logged in
    }
    if (!this.dialogOpen) {
      // Broadcast a message to open the dialog in other windows
      this.broadcastChannelService.postMessage('open-dialog');

      this.dialogOpen = true;

      this.sessionExpireDialogRef = this.dialog.open(SessionExpireComponent, {
        width: '400px',
        disableClose: true
      });

      // Clear any existing dialog timer to prevent overlap
      if (this.dialogOpenTimer) {
        clearTimeout(this.dialogOpenTimer);
        this.dialogOpenTimer = null;
      }

      // Start a timer to auto-logout after 5 minutes of dialog being open
      this.dialogOpenTimer = setTimeout(() => {
        this.logout();
        if (this.sessionExpireDialogRef) {
          this.sessionExpireDialogRef.close();
        }
      }, 5 * 60 * 1000); // 5 minutes

      this.sessionExpireDialogRef?.afterClosed().subscribe((result) => {
        this.dialogOpen = false;
        clearTimeout(this.dialogOpenTimer); // Clear the dialog timer when closed
        if (result === 'extend') {
          this.extendSession();
        } else if (result === 'logout') {
          this.logout();
        }
      });
    }
  }

  /**
   * Extends the session by calling the AuthService to refresh the token.
   */
  extendSession(): void {
    if (this.isExtendingSession) { // Session extension in progress
      return;
    }

    if (!this.isAuthenticated()) {
      this.logout(); // Cannot extend a session that was never authenticated
      return;
    }

    this.isExtendingSession = true;
    this.pauseTimers(); // Pause timers during session extension

    this.authService.refreshToken().subscribe({
      next: (response) => {
        const jwtExpiresAt = Number(response.expiresAt);

        if (jwtExpiresAt) {
          // JWT expires at 15 minutes, but business requires 25 minutes of inactivity before warning message and expires at 30 minutes
          const additionalTime = 15 * 60 * 1000; // 15 minutes in milliseconds
          const newExpiration = jwtExpiresAt + additionalTime;

          const sessionDuration = Number(newExpiration) - Date.now(); // Calculate session duration

          localStorage.setItem('sessionExpiration', newExpiration.toString()); // Update the expiration time
          localStorage.setItem('sessionDuration', sessionDuration.toString()); // Store the session duration
          this.resumeTimers(); // Resume timers after successful extension
          this.resetInactivityTimer(); // Restart the inactivity timer
          this.startSessionExpirationCheck(); // Restart the session expiration check
        }
        this.isExtendingSession = false;
      },
      error: (err) => {
        console.error('Error details:', {
          status: err.status,
          message: err.message,
          body: err.error,
        });

        this.isExtendingSession = false;

        if (err.status === 400) {
          console.warn('Session refresh failed with 400 Bad Request. Logging out.');
          this.logout(); // Log out if the session cannot be extended
        }
      },
      complete: () => {
        console.log("IntervalSessionService.extendSession() complete()");
        this.isExtendingSession = false; // Ensure the flag is reset on completion
      }
    });
  }

  public pauseTimers(): void {
    console.log("IntervalSessionService.pauseTimers()");
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.expirationTimer) {
      this.expirationTimer.unsubscribe();
      this.expirationTimer = null;
    }
  }

  public resumeTimers(): void {
    this.resetInactivityTimer();
    this.startSessionExpirationCheck();
  }

  /**
   * Logs the user out and redirects to the login page.
   */
  logout(): void {
    if (this.isExtendingSession) { // Session extension in progress
      return;
    }

    if (this.expirationTimer) {
      this.expirationTimer.unsubscribe(); // Clear the expiration timer
    }

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer); // Clear the inactivity timer
      this.inactivityTimer = null;
    }

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer); // Clear the proactive refresh timer
      this.refreshTimer = null;
    }
    
    if (this.dialogOpenTimer) {
      clearTimeout(this.dialogOpenTimer);
      this.dialogOpenTimer = null;
    }

    window.removeEventListener('storage', this.handleStorageEvent);

    if (this.authService.getVerifyId()) { // Prevent logout if the user is not logged in
      this.authService.logout(); // Perform logout actions
    }
  }

  /**
   * Handles storage events to synchronize session expiration across browser tabs/windows.
   * @param event The storage event triggered by changes to localStorage.
   */
  private handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'sessionExpiration') {
      const newExpiration = parseInt(event.newValue || '0', 10);

      if (newExpiration > Date.now()) { // Restart the session expiration check
        // Debounce session expiration synchronization
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = setTimeout(() => {
          if (this.authService.getVerifyId()) {
            this.resetInactivityTimer();
            this.startSessionExpirationCheck();
          }
        }, 100); // Debounce interval
      } else {
        this.logout(); // Expired session
      }

      this.sessionExpireDialogRef?.close(); // Close the dialog if open
    }

    if (event.key === 'dialogOpen' && event.newValue === 'false') {
      this.dialogOpen = false; // Synchronize dialog state across tabs
    }
  }

  public closeDialog(): void {
    this.sessionExpireDialogRef?.close();
  }
}